/**
 * Portail membre — carte « Ma cotisation » : appel de cotisation en cours, règlement CB / SEPA 10× / virement.
 * SEPA : signature du mandat en ligne (contrat « mandat SEPA » créé à la demande), puis acceptation de l'échéancier
 * (l'accueil confirme la programmation en un clic).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { CreditCard, Landmark, Building2, Check, Loader2, FileSignature, Receipt, FileText, Download } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';
const money = (v) => `${Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString('fr-FR') : '');
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('client_portal_token')}`, 'Content-Type': 'application/json' });

const card = { backgroundColor: 'rgba(255,255,255,0.92)', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' };

// Ouvre un PDF protégé (reçu / facture) dans un nouvel onglet via blob authentifié
const openPdf = async (url) => {
  try {
    const r = await fetch(`${API}${url}`, { headers: authHeaders() });
    if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.detail || 'Document indisponible'); }
    const blobUrl = URL.createObjectURL(await r.blob());
    window.open(blobUrl, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  } catch (e) { toast.error(e.message); }
};

const DocButtons = ({ receipt, showInvoice }) => receipt ? (
  <div className="flex flex-wrap gap-1.5 mt-2" data-testid="cotisation-docs">
    {showInvoice && receipt.invoice_url && (
      <button type="button" onClick={() => openPdf(receipt.invoice_url)} className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold text-white" style={{ backgroundColor: '#2E6A4C' }} data-testid="cotisation-invoice-btn">
        <FileText size={12} /> Ma facture (PDF)
      </button>
    )}
    <button type="button" onClick={() => openPdf(receipt.ticket_url)} className="inline-flex items-center gap-1 rounded-lg border border-emerald-300 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-emerald-800" data-testid="cotisation-receipt-btn">
      <Download size={12} /> Reçu de paiement{receipt.number ? ` n° ${receipt.number}` : ''}
    </button>
  </div>
) : null;

const Option = ({ icon: Icon, title, active, children, testId }) => (
  <div className={`rounded-xl border p-3 ${active ? 'border-[#2E6A4C] bg-[#2E6A4C]/5' : 'border-neutral-200'}`} data-testid={testId}>
    <div className="flex items-center gap-2 mb-1.5">
      <Icon size={14} style={{ color: '#2E6A4C' }} />
      <span className="text-xs font-semibold text-neutral-800">{title}</span>
      {active && <span className="ml-auto text-[10px] font-medium text-[#2E6A4C]">Votre choix</span>}
    </div>
    {children}
  </div>
);

const copy = (text, label) => navigator.clipboard?.writeText(label === 'IBAN' ? String(text || '').replace(/\s/g, '') : String(text || '')).then(() => toast.success(`Copié : ${label}`)).catch(() => {});

const RibLine = ({ label, value, mono, testId }) => (
  <div className="flex items-center justify-between gap-2">
    <span className="text-neutral-500 shrink-0">{label}</span>
    <button type="button" onClick={() => copy(value, label)} className={`text-right truncate hover:underline ${mono ? 'font-mono' : ''} font-semibold text-neutral-900`} title="Copier" data-testid={testId}>{value}</button>
  </div>
);

// Coordonnées du virement (titulaire, IBAN, BIC, montant, référence) — un clic copie la valeur
const TransferDetails = ({ transfer }) => (
  <div className="text-[11px] space-y-0.5" data-testid="cotisation-transfer-details">
    {transfer.iban ? (
      <>
        {transfer.holder && <RibLine label="Titulaire" value={transfer.holder} testId="cotisation-transfer-holder" />}
        <RibLine label="IBAN" value={transfer.iban} mono testId="cotisation-transfer-iban" />
        <RibLine label="BIC" value={transfer.bic} mono testId="cotisation-transfer-bic" />
        <RibLine label="Montant" value={money(transfer.amount)} testId="cotisation-transfer-amount" />
      </>
    ) : <div className="text-neutral-600">Coordonnées bancaires disponibles à l'accueil.</div>}
    <RibLine label="Référence à indiquer" value={transfer.reference} testId="cotisation-transfer-reference" />
    {transfer.iban && <p className="text-[10px] text-neutral-400 pt-0.5">Touchez une valeur pour la copier.</p>}
  </div>
);

export default function CotisationCallCard({ refreshKey = 0, onMandateRequested, onVisibilityChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/client-portal/cotisation-call`, { headers: authHeaders() });
      if (r.ok) setData(await r.json());
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => { if (!loading) onVisibilityChange?.(!!data?.dossier); }, [loading, data, onVisibilityChange]);

  const requestMandate = async () => {
    setBusy('mandate');
    try {
      const r = await fetch(`${API}/api/client-portal/cotisation-call/sepa-mandate`, { method: 'POST', headers: authHeaders() });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Erreur');
      toast.success(j.existing ? 'Votre mandat est prêt à signer ci-dessous' : 'Mandat SEPA préparé : signez-le ci-dessous (IBAN + signature)');
      onMandateRequested?.(j.contract_id);
      await load();
    } catch (e) { toast.error(e.message); } finally { setBusy(''); }
  };

  const acceptSepa = async () => {
    const s = data.sepa;
    if (!window.confirm(`Accepter le prélèvement de votre cotisation en ${s.installments} mensualités de ${money(s.monthly)} à partir du ${fmtDate(s.first_debit_date)} ?`)) return;
    setBusy('accept');
    try {
      const r = await fetch(`${API}/api/client-portal/cotisation-call/accept-sepa`, { method: 'POST', headers: authHeaders() });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Erreur');
      setData(j);
      toast.success('Échéancier accepté : l\'accueil programme vos prélèvements et vous recevrez votre facture par email.');
    } catch (e) { toast.error(e.message); } finally { setBusy(''); }
  };

  const declareTransfer = async () => {
    if (!window.confirm(`Confirmez-vous avoir fait le virement de ${money(data.transfer.amount)} avec la référence ${data.transfer.reference} ?`)) return;
    setBusy('transfer');
    try {
      const r = await fetch(`${API}/api/client-portal/cotisation-call/transfer-declared`, { method: 'POST', headers: authHeaders() });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Erreur');
      setData(j);
      toast.success('Merci ! L\'accueil confirmera la réception de votre virement.');
    } catch (e) { toast.error(e.message); } finally { setBusy(''); }
  };

  const payOnline = async () => {
    setBusy('online');
    try {
      const r = await fetch(`${API}/api/client-portal/cotisation-call/pay-online`, { method: 'POST', headers: authHeaders() });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Erreur');
      toast.info('Redirection vers le paiement sécurisé…', { description: `Montant : ${money(j.amount)}` });
      window.location.href = j.checkout_url;
    } catch (e) { toast.error(e.message); setBusy(''); }
  };

  if (loading || !data?.dossier) return null;
  const { dossier: d, campaign: c, sepa, transfer, online } = data;
  const paid = d.status === 'paid';
  const cbReceived = !paid && d.online_payment?.status === 'received';
  const sepaChosen = !paid && !!d.sepa_accepted && sepa.has_active_mandate;
  const transferDeclared = !paid && !!d.transfer_declared;
  const paying = busy === 'online';
  const paidVia = d.paid_via === 'sepa' ? 'par prélèvement SEPA' : d.paid_via === 'portail' ? 'par carte bancaire' : d.paid_via === 'caisse' ? 'à l\'accueil' : d.paid_via === 'transfer' ? 'par virement' : d.paid_via === 'check' ? 'par chèque' : d.paid_via === 'cash' ? 'en espèces' : '';
  const activeUntil = d.active_until || c.season_end;

  return (
    <div className="rounded-2xl p-4 space-y-3" style={card} data-testid="cotisation-call-card">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h4 className="font-bold uppercase" style={{ fontSize: '11px', color: '#999', letterSpacing: '0.08em' }}>Ma cotisation {c.season_year}</h4>
          <p className="text-sm font-semibold text-neutral-900 mt-0.5">{d.cotisation_label}{d.company ? ` — ${d.company}` : ''}</p>
          {d.members?.length > 1 && <p className="text-[11px] text-neutral-500">{d.members.join(' & ')}</p>}
        </div>
        <div className="text-right">
          <div className="text-lg font-bold" style={{ color: '#2E6A4C' }} data-testid="cotisation-call-total">{money(d.total_ttc)}</div>
          <div className="text-[10px] text-neutral-500">{paid ? `réglée le ${fmtDate(d.paid_at)}` : sepaChosen ? 'prélèvement SEPA choisi' : transferDeclared ? 'virement annoncé' : `à régler avant le ${fmtDate(c.due_date)}`}</div>
        </div>
      </div>

      {(d.options?.length > 0 || Number(d.discount?.amount) > 0) && (
        <ul className="text-[11px] text-neutral-600 space-y-0.5">
          <li>Cotisation : {money(d.amount_ttc)}</li>
          {d.options.map((o, i) => <li key={i}>{o.label} : {money(o.amount)}</li>)}
          {Number(d.discount?.amount) > 0 && <li>Remise {d.discount.label} : - {money(d.discount.amount)}</li>}
        </ul>
      )}

      {paid ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800" data-testid="cotisation-call-paid">
          <div className="flex items-start gap-2">
            <span className="mt-0.5 w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center shrink-0"><Check size={13} /></span>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-emerald-900" data-testid="cotisation-paid-title">Cotisation payée le {fmtDate(d.paid_at)}{paidVia ? ` ${paidVia}` : ''}</div>
              {d.paid_via === 'sepa' && d.sepa && (
                <div className="text-[11px] mt-0.5 flex items-center gap-1" data-testid="cotisation-paid-sepa-plan">
                  <Landmark size={11} /> {d.sepa.installments} prélèvements de {money(d.sepa.monthly)} à partir du {fmtDate(d.sepa.first_debit_date)}
                </div>
              )}
              <div className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-white/80 border border-emerald-200 px-2.5 py-1 text-[11px] font-semibold text-emerald-800" data-testid="cotisation-active-until">
                <Check size={11} /> Cotisation active jusqu'au {fmtDate(activeUntil)}
              </div>
              <div className="text-[10px] text-emerald-700 mt-1.5">{d.receipt ? 'Votre facture est disponible ci-dessous et dans « Mes paiements ».' : 'Votre facture vous a été remise par l\'accueil.'}</div>
              <DocButtons receipt={d.receipt} showInvoice />
            </div>
          </div>
        </div>
      ) : sepaChosen ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800" data-testid="cotisation-call-sepa-chosen">
          <div className="flex items-start gap-2">
            <span className="mt-0.5 w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center shrink-0"><Landmark size={13} /></span>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-emerald-900">Prélèvement SEPA en {d.sepa_accepted.installments} fois — accepté le {fmtDate(d.sepa_accepted.at)}</div>
              <div className="text-[11px] mt-0.5">{d.sepa_accepted.installments} mensualités de <b>{money(d.sepa_accepted.monthly)}</b> à partir du {fmtDate(d.sepa_accepted.first_debit_date)} · mandat {sepa.mandate?.umr} · compte {sepa.mandate?.iban_masked}</div>
              <div className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-white/80 border border-amber-200 px-2.5 py-1 text-[11px] font-semibold text-amber-800" data-testid="cotisation-sepa-pending">
                <Loader2 size={11} className="animate-spin" /> En attente de confirmation par l'accueil
              </div>
              <div className="text-[10px] text-emerald-700 mt-1.5">Dès la confirmation, votre cotisation {c.season_year} sera active et votre facture vous parviendra par email. Pour changer de moyen de paiement, contactez l'accueil.</div>
            </div>
          </div>
        </div>
      ) : transferDeclared ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3 text-xs text-neutral-800" data-testid="cotisation-call-transfer-declared">
          <div className="flex items-start gap-2">
            <span className="mt-0.5 w-6 h-6 rounded-full bg-amber-500 text-white flex items-center justify-center shrink-0"><Building2 size={13} /></span>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-neutral-900">Virement annoncé le {fmtDate(d.transfer_declared.at)}</div>
              <div className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-white/80 border border-amber-200 px-2.5 py-1 text-[11px] font-semibold text-amber-800" data-testid="cotisation-transfer-pending">
                <Loader2 size={11} className="animate-spin" /> En attente de réception par l'accueil
              </div>
              <div className="text-[10px] text-neutral-600 mt-1.5">Dès réception sur notre compte, votre cotisation {c.season_year} sera active et votre facture vous parviendra par email. Pour changer de moyen de paiement, contactez l'accueil.</div>
              <div className="mt-2"><TransferDetails transfer={transfer} /></div>
            </div>
          </div>
        </div>
      ) : cbReceived ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800 flex items-start gap-2" data-testid="cotisation-call-cb-received">
          <Check size={14} className="mt-0.5" />
          <div>
            Paiement par carte de {money(d.online_payment.amount)} reçu le {fmtDate(d.online_payment.paid_at)}. Merci !
            <div className="text-[10px] text-emerald-700 mt-0.5">{d.receipt ? 'Votre reçu est disponible immédiatement ; ' : 'Votre reçu arrive dans quelques instants ; '}votre facture sera disponible ici et dans « Mes paiements » dès l'enregistrement de votre règlement par l'accueil (et envoyée par email).</div>
            <DocButtons receipt={d.receipt} showInvoice={false} />
          </div>
        </div>
      ) : (
        <div className="grid gap-2">
          {d.last_payment_mode && !d.payment_mode && d.status !== 'paid' && (
            <p className="text-[11px] text-neutral-500 italic" data-testid="cotisation-last-paymode">L'an dernier, vous aviez réglé par {({ online: 'carte bancaire en ligne', sepa: 'prélèvement SEPA', transfer: 'virement', cheque: 'chèque', cash: 'espèces' })[d.last_payment_mode] || d.last_payment_mode}. Choisissez librement votre moyen de paiement ci-dessous.</p>
          )}
          {online.enabled && (
            <Option icon={CreditCard} title="Carte bancaire en ligne" active={d.payment_mode === 'online'} testId="cotisation-option-online">
              <button onClick={payOnline} disabled={!!busy} className="w-full rounded-lg py-2 text-xs font-semibold text-white disabled:opacity-60" style={{ backgroundColor: '#2E6A4C' }} data-testid="cotisation-pay-online-btn">
                {paying ? <Loader2 size={13} className="inline animate-spin mr-1" /> : null}Payer {money(d.total_ttc)} par carte
              </button>
            </Option>
          )}
          {sepa.enabled && (
            <Option icon={Landmark} title={`Prélèvement SEPA en ${sepa.installments} fois`} active={d.payment_mode === 'sepa'} testId="cotisation-option-sepa">
              <p className="text-[11px] text-neutral-600 mb-2">{sepa.installments} mensualités de <b>{money(sepa.monthly)}</b> à partir du {fmtDate(sepa.first_debit_date)}.</p>
              {d.sepa_accepted && sepa.has_active_mandate ? (
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-2 text-[11px] text-emerald-800 flex items-start gap-1.5" data-testid="cotisation-sepa-accepted">
                  <Check size={12} className="mt-0.5" /> Échéancier accepté le {fmtDate(d.sepa_accepted.at)}. L'accueil programme vos prélèvements ; votre facture suivra par email.
                </div>
              ) : sepa.has_active_mandate ? (
                <>
                  <p className="text-[10px] text-neutral-500 mb-1.5">Mandat {sepa.mandate?.umr} · compte {sepa.mandate?.iban_masked}</p>
                  <button onClick={acceptSepa} disabled={!!busy} className="w-full rounded-lg py-2 text-xs font-semibold text-white disabled:opacity-60" style={{ backgroundColor: '#2E6A4C' }} data-testid="cotisation-accept-sepa-btn">
                    {busy === 'accept' ? <Loader2 size={13} className="inline animate-spin mr-1" /> : null}J'accepte le prélèvement en {sepa.installments} fois
                  </button>
                </>
              ) : sepa.pending_contract_id ? (
                <button onClick={() => onMandateRequested?.(sepa.pending_contract_id)} className="w-full rounded-lg py-2 text-xs font-semibold border border-[#2E6A4C] text-[#2E6A4C] flex items-center justify-center gap-1.5" data-testid="cotisation-sign-mandate-btn">
                  <FileSignature size={13} /> Signer mon mandat SEPA (en attente ci-dessous)
                </button>
              ) : (
                <button onClick={requestMandate} disabled={!!busy} className="w-full rounded-lg py-2 text-xs font-semibold border border-[#2E6A4C] text-[#2E6A4C] flex items-center justify-center gap-1.5 disabled:opacity-60" data-testid="cotisation-request-mandate-btn">
                  {busy === 'mandate' ? <Loader2 size={13} className="animate-spin" /> : <FileSignature size={13} />} Mettre en place le prélèvement (signature en ligne)
                </button>
              )}
            </Option>
          )}
          {transfer.enabled && (
            <Option icon={Building2} title="Virement bancaire" active={d.payment_mode === 'transfer'} testId="cotisation-option-transfer">
              <TransferDetails transfer={transfer} />
              {transfer.iban && (
                <button onClick={declareTransfer} disabled={!!busy} className="mt-2 w-full rounded-lg py-2 text-xs font-semibold border border-[#2E6A4C] text-[#2E6A4C] flex items-center justify-center gap-1.5 disabled:opacity-60" data-testid="cotisation-declare-transfer-btn">
                  {busy === 'transfer' ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} J'ai fait mon virement
                </button>
              )}
            </Option>
          )}
          <p className="text-[10px] text-neutral-400 flex items-center gap-1"><Receipt size={10} /> Appel de cotisation n° {d.number} — une facture vous sera remise lors du règlement.</p>
        </div>
      )}
    </div>
  );
}
